import './styles.scss'
import React, { useContext, useEffect, useRef, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import { v4 as uuidv4 } from 'uuid'
import remarkGfm from 'remark-gfm' // dynamically imported below to prevent UI crash and reduce bundle size (it was crashing ipad ui)
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter'
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism'
import { useTranslation } from 'react-i18next'
import { Button } from 'primereact/button'
import { InputTextarea } from 'primereact/inputtextarea'
import { Sidebar } from 'primereact/sidebar'
import { getLocalUserDetails } from 'Utils/LocalStorage_Handler'
import { CoffeeWebContext } from 'Context/CoffeeWebContext'
import apiAdapterCoffeeWeb from 'Services/apiAdapter_CoffeeWeb'
import { CoffeeBeanIcon } from 'Assets/Icons'
import { useResizeHandler } from 'Utils/commonMethods'

// Dynamically loads remark-gfm to prevent iPad/Safari UI crashes and reduce initial bundle size
let remarkGfmPlugin = null

const loadRemarkGfm = async () => {
  if (!remarkGfmPlugin) {
    const { default: remarkGfm } = await import('remark-gfm').catch(() => ({ default: null }))

    remarkGfmPlugin = remarkGfm
  }

  return remarkGfmPlugin
}

const CoffeeWebAi = () => {
  const { t } = useTranslation()
  const listRef = useRef(null)
  const { isWebView } = useResizeHandler()
  const PAGE_SIZE = 10
  const userDetails = getLocalUserDetails()
  const guestIdRef = useRef(uuidv4())
  const inputRef = useRef(null)

  const { setLoading } = useContext(CoffeeWebContext)
  const [messages, setMessages] = useState([])
  const [subjectList, setSubjectList] = useState([])
  const [promptList, setPromptList] = useState([])
  const [disabledPrompts, setDisabledPrompts] = useState([])
  const [draggedIndex, setDraggedIndex] = useState(null)
  const [showContent, setShowContent] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)
  const [copied, setCopied] = useState(false)
  const [buttonDisabled, setButtonDisabled] = useState(false)
  const [visible, setVisible] = useState(false)
  const [value, setValue] = useState('')
  const [subjectTitle, setSubjectTitle] = useState([])
  const [sideBarPosition, setSideBarPosition] = useState('')
  const [pageNumber, setPageNumber] = useState(1)
  const [hasMore, setHasMore] = useState(true)
  const [loadingHistory, setLoadingHistory] = useState(false)
  const [historyLoaded, setHistoryLoaded] = useState(false)
  const [remarkGfmReady, setRemarkGfmReady] = useState(false)

  useEffect(() => {
    getHistoryChatData()
    fetchAllPromptsQuestion()
    loadRemarkGfm().then(() => setRemarkGfmReady(true))
  }, [])

  const getHistoryChatData = async ({ initial = false } = {}) => {
    if (loadingHistory || !hasMore) return
    setLoadingHistory(true)
    setShowContent(false)

    const container = listRef.current
    const prevScrollHeight = initial ? 0 : container.scrollHeight
    const prevScrollTop = initial ? 0 : container.scrollTop

    try {
      const response = await apiAdapterCoffeeWeb.gitChatHistoryData({
        id: userDetails?.id,
        pageNumber,
        pageSize: PAGE_SIZE
      })

      const newResponse = response?.data?.items || []

      if (newResponse.length === 0) {
        setHasMore(false)

        return
      }

      const formatted = newResponse
        .map((item) => ({
          id: item.mongoId,
          question: item.prompt,
          answer: item.response,
          created: formatTime(item.created),
          formattedDate: formatDate(item.created)
        }))
        .reverse()

      setMessages((prev) => {
        const existingIds = new Set(prev.map((m) => m.id))
        const uniqueNew = formatted.filter((m) => !existingIds.has(m.id))

        return [...uniqueNew, ...prev]
      })

      setPageNumber((p) => (p === pageNumber ? p + 1 : p))
    } catch (e) {
      console.error('Error fetching history:', e)
    } finally {
      setLoadingHistory(false)
      setHistoryLoaded(true)

      requestAnimationFrame(() => {
        if (!container) return

        if (initial) {
          requestAnimationFrame(() => {
            setTimeout(() => {
              if (container) {
                container.scrollTop = container.scrollHeight
              }
            }, 0) // Safari sometimes needs this delay
          })
        } else {
          requestAnimationFrame(() => {
            const newScrollHeight = container.scrollHeight
            const diff = newScrollHeight - prevScrollHeight

            container.scrollTop = prevScrollTop + diff
          })
        }
      })
    }
  }

  const fetchAllPromptsQuestion = async () => {
    setLoading(true)
    try {
      const response = await apiAdapterCoffeeWeb.getAllFAQPromptsQuestion()

      setSubjectList(response?.data?.firstQuestionsSet)
      setPromptList(response?.data?.secondQuestionsSet)
      setSubjectTitle(response?.data?.categories)
    } finally {
      setLoading(false)
    }
  }

  const postPromptQuestion = async () => {
    setButtonDisabled(true)

    if (!value.trim()) return

    const questionText = value

    setValue('')
    setDisabledPrompts((prevDisabledPrompts) => [...prevDisabledPrompts, questionText])

    const now = new Date()

    const thinkingMessage = {
      question: questionText,
      answer: 'Brewing',
      isPending: true,
      created: formatTime(now),
      formattedDate: formatDate(now)
    }

    let messageIndex = -1

    setMessages((prevMessages) => {
      const updatedMessages = [...prevMessages, thinkingMessage]

      messageIndex = updatedMessages.length - 1
      setTimeout(() => {
        scrollToBottom()
      }, 0)

      return updatedMessages
    })

    const promptData = {
      prompt: questionText,
      userid: userDetails?.id?.toString() || guestIdRef?.current
    }

    try {
      const response = await apiAdapterCoffeeWeb.postQuestion(promptData)
      const newResponse = response?.data?.aiResponse

      if (newResponse) {
        setButtonDisabled(false)
      }

      const finalMessage = {
        question: newResponse?.prompt,
        answer: newResponse?.response,
        created: formatTime(newResponse?.created),
        formattedDate: formatDate(newResponse.created)
      }

      setMessages((prevMessages) => {
        const updatedMessages = [...prevMessages]

        updatedMessages[messageIndex] = finalMessage

        return updatedMessages
      })
    } catch {
      setButtonDisabled(false)
    }
  }

  const scrollToBottom = () => {
    if (listRef.current) {
      listRef.current.scrollTo({
        top: listRef.current.scrollHeight,
        behavior: 'smooth'
      })
    }
  }

  const handleDragStart = (index) => {
    setDraggedIndex(index)
  }

  const handleDragOver = (e) => {
    e.preventDefault()
  }

  const handleDrop = (index, listType) => {
    if (draggedIndex === null || draggedIndex === index) return

    const list = listType === 'subject' ? [...subjectList] : [...promptList]
    const draggedItem = list[draggedIndex]

    list.splice(draggedIndex, 1)
    list.splice(index, 0, draggedItem)

    listType === 'subject' ? setSubjectList(list) : setPromptList(list)
    setDraggedIndex(null)
  }

  const handleSend = () => {
    if (value.trim() === '') return

    setShowContent(false)
    postPromptQuestion()
  }
  const handleScroll = () => {
    if (!listRef.current || loadingHistory || !hasMore) return
    if (listRef.current.scrollTop <= 10) {
      getHistoryChatData()
    }
  }

  const handleChange = (newValue) => {
    const formattedValue = newValue.charAt(0).toUpperCase() + newValue.slice(1)

    setValue(formattedValue)

    const textarea = document.querySelector('.input-field')

    if (textarea) {
      textarea.style.height = 'auto'

      const newHeight = Math.min(textarea.scrollHeight, 100)

      textarea.style.height = `${newHeight}px`

      textarea.style.overflowY = textarea.scrollHeight > 100 ? 'auto' : 'hidden'
    }
  }

  const handlePromptClick = (prompt) => {
    setValue(prompt)
    setVisible(false)
    setTimeout(() => {
      if (inputRef?.current) {
        const el = inputRef?.current?.getInput ? inputRef?.current?.getInput() : inputRef?.current

        el.focus()
        const length = el?.value?.length

        el.setSelectionRange(length, length)
      }
    }, 0)
  }

  const handleEditClick = (value) => {
    setValue(value)
    setTimeout(() => {
      if (inputRef?.current) {
        const el = inputRef?.current?.getInput ? inputRef?.current?.getInput() : inputRef?.current

        el.focus()
        const length = el?.value?.length

        el.setSelectionRange(length, length)
      }
    }, 0)
  }

  const copyToClipboard = (text) => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text)
      setCopied(true)

      setTimeout(() => {
        setCopied(false)
      }, 3000)
    }
  }

  const formatTime = (timestamp) => {
    const date = new Date(timestamp)

    const options = {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    }

    return date.toLocaleTimeString('en-US', options)
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  const formatDate = (timestamp) => {
    const date = new Date(timestamp)
    const today = new Date()

    const isToday = date.getDate() === today.getDate() && date.getMonth() === today.getMonth() && date.getFullYear() === today.getFullYear()

    const options = {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    }

    if (isToday) {
      return 'Today'
    }

    return date.toLocaleDateString('en-US', options)
  }

  const groupedMessages = messages?.reduce((groups, message) => {
    const date = message.formattedDate

    return {
      ...groups,
      [date]: [...(groups[date] || []), message]
    }
  }, {})

  const handleDrawerOpen = (prompt) => {
    if (prompt === 'subject') {
      setSideBarPosition('left')
    } else {
      setSideBarPosition('right')
    }

    setVisible(true)
  }
  const handleDrawerClose = () => {
    setVisible(false)
  }

  const renderPromptList = (list, listType) => (
    <ul>
      {list?.map((item, index) => (
        <li key={index} className={disabledPrompts?.includes(item) ? 'disabled' : ''} onClick={() => !disabledPrompts?.includes(item) && handlePromptClick(item)}>
          <span>{item}</span>
        </li>
      ))}
    </ul>
  )

  return (
    <div className="coffee-web-ai-container">
      <div className="sidebar">
        <h3 className="sidebar-title">{t('PROMPT_TITLE')}</h3>

        <div className="subject-list-wrapper">
          <ul>
            {subjectList?.map((subj, index) => (
              <li key={index} draggable onDragStart={() => handleDragStart(index)} onDragOver={handleDragOver} onDrop={() => handleDrop(index, 'subject')} className={disabledPrompts.includes(subj) ? 'disabled' : ''} onClick={() => !disabledPrompts.includes(subj) && handlePromptClick(subj)}>
                <span>{subj}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
      <div className="chat-section">
        <div className="page-header">
          <div className="page-header-icon-right" onClick={() => handleDrawerOpen('subject')}>
            <i className="pi pi-arrow-right right-icon" />
          </div>
          <div className="page-title">
            <h2>{t('COFFEE_WEB_AI_TITLE')}</h2>
          </div>
          <div className="page-header-icon-left" onClick={() => handleDrawerOpen('prompt')}>
            <i className="pi pi-arrow-left left-icon" />
          </div>
        </div>

        <div className={`chat-box - ${historyLoaded && (showContent || messages.length === 0) ? 'welcome-text-content' : ''}`} ref={listRef} onScroll={handleScroll}>
          {historyLoaded && (showContent || messages.length === 0) && (
            <div className="welcome-text">
              <span className="welcome-title">{t('WELCOME_TITLE')}</span>
              <span className="welcome-subtitle">{t('WELCOME_SUB_TITLE')}</span>
            </div>
          )}

          {Object?.entries(groupedMessages).map(([date, msgs]) => (
            <div key={date} className="date-group">
              <div className="date-separator">
                <span className="date-label">{date}</span>
              </div>

              {msgs?.map(
                (msg, idx) =>
                  msg?.question?.trim() &&
                  msg?.answer?.trim() && (
                    <div key={idx} className="chat-message">
                      <div className="question-box">
                        <div className="question-header">
                          <p>{msg?.question}</p>
                          <i className="pi pi-pen-to-square edit-icon" onClick={() => handleEditClick(msg?.question)} />
                        </div>
                        <div className="created-time">
                          <p>{msg?.created}</p>
                        </div>
                      </div>

                      <div className="answer-box">
                        <div className="logo-container">
                          <img src="https://coffeeweb.s3.ap-south-1.amazonaws.com/Logo+Mark.png" alt="coffee-web-logo" className="bullet-logo" />
                        </div>
                        <div className="message-container">
                          <div className={`markdown-wrapper ${msg?.isPending ? 'thinking-text' : ''}`}>
                            {msg?.isPending ? (
                              <span className="thinking-text">
                                <span className="brewing-dots">
                                  <CoffeeBeanIcon className="dot" />
                                  <CoffeeBeanIcon className="dot" />
                                  <CoffeeBeanIcon className="dot" />
                                  <CoffeeBeanIcon className="dot" />
                                </span>
                                {t('BREWING')}
                              </span>
                            ) : (
                              <ReactMarkdown
                                remarkPlugins={remarkGfmReady && remarkGfmPlugin ? [remarkGfmPlugin] : []}
                                components={{
                                  code({ node, inline, className, children, ...props }) {
                                    const match = /language-(\w+)/.exec(className || '')

                                    return !inline && match ? (
                                      <SyntaxHighlighter style={oneDark} language={match[1]} PreTag="div" {...props}>
                                        {String(children).replace(/\n$/, '')}
                                      </SyntaxHighlighter>
                                    ) : (
                                      <code className="inline-code" {...props}>
                                        {children}
                                      </code>
                                    )
                                  },
                                  table({ children }) {
                                    return <table className="markdown-table">{children}</table>
                                  },
                                  th({ children }) {
                                    return <th className="markdown-th">{children}</th>
                                  },
                                  td({ children }) {
                                    return <td className="markdown-td">{children}</td>
                                  },
                                  a({ node, ...props }) {
                                    return (
                                      <a target="_blank" rel="noopener noreferrer" {...props} aria-label="Link to external content">
                                        {props.children || 'Link'}
                                      </a>
                                    )
                                  }
                                }}
                              >
                                {msg?.answer}
                              </ReactMarkdown>
                            )}
                          </div>

                          {!msg?.isPending && (
                            <div className="action-container">
                              <div className="copy-wrapper hover-on-copy">
                                <i className={`pi ${copied ? 'pi-check' : 'pi-clone'} copy-icon`} onClick={() => copyToClipboard(msg.answer)} />
                                <div className="tool-tip-copy">
                                  <div>{copied ? 'Copied' : 'COPY'}</div>
                                </div>
                              </div>

                              <div className="created-time">{msg?.created}</div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )
              )}
            </div>
          ))}
        </div>

        <div className="chat-input">
          <div className="input-wrapper">
            <img src="https://coffeeweb.s3.ap-south-1.amazonaws.com/Logo+Mark.png" alt="coffee-web-logo" className="input-logo" />
            <InputTextarea ref={inputRef} rows={isWebView ? 1 : 2} cols={1} value={value} onKeyDown={handleKeyDown} onChange={(e) => handleChange(e.target.value)} placeholder={t('PLACE_HOLDER_TEXT')} className="input-field" />
            <Button rounded icon="pi pi-send send-icon" className="send-button" onClick={handleSend} disabled={buttonDisabled} />
          </div>
        </div>
      </div>

      <div className="prompts">
        <h3 className="prompts-title">{t('PROMPT_TITLE')}</h3>

        <div className="prompts-list-wrapper">
          <ul>
            {promptList?.map((prompt, index) => (
              <li key={index} draggable onDragStart={() => handleDragStart(index)} onDragOver={handleDragOver} onDrop={() => handleDrop(index, 'prompt')} className={disabledPrompts.includes(prompt) ? 'disabled' : ''} onClick={() => !disabledPrompts.includes(prompt) && handlePromptClick(prompt)}>
                <span className="prompt-text">{prompt}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <Sidebar visible={visible} onHide={handleDrawerClose} className="prompt-sidebar" position={sideBarPosition} showCloseIcon={false}>
        <div className="sidebar-header">
          <div className="sidebar-title">
            <span>{sideBarPosition === 'left' ? subjectTitle[0] : subjectTitle[1]}</span>
          </div>
          <div className="close-icon">
            <i className="pi pi-times" onClick={() => setVisible(false)} />
          </div>
        </div>
        <div className="sidebar-content">
          {sideBarPosition === 'left' && <>{renderPromptList(subjectList, 'subject')}</>}

          {sideBarPosition === 'right' && <>{renderPromptList(promptList, 'prompt')}</>}
        </div>
      </Sidebar>
    </div>
  )
}

export default CoffeeWebAi
